// Which documents an employment application needs, and whether it has them.
//
// The one owner of that rule. The public form (ApplyForm) asks for them, the
// complete-staff-application function refuses to mark an application received
// without them, and the Onboarding Pipeline and the Daily Digest say which are
// missing from an application that was started and never finished.
//
// Since 2026-10-27 an application is saved the moment the applicant presses
// Submit, as `incomplete`, and each document is written onto it as it
// arrives. Before that the documents had to arrive first, and an applicant
// whose phone photos timed out (Christina Chambers, four attempts) left no
// record at all. Runtime-neutral: imported by the app and, through
// _shared/lib, by edge functions.

/** An application saved before its documents have all arrived. */
export const APPLICATION_INCOMPLETE = 'incomplete';
/** Received and waiting for the office — what the pipeline has always called pending. */
export const APPLICATION_RECEIVED = 'pending';

/** How long an incomplete application stays open to its applicant. */
export const APPLICATION_OPEN_HOURS = 24;

export const LICENSE_REQUIRED_POSITIONS = ['rn', 'lpn'];
export const licenseRequired = (position) => LICENSE_REQUIRED_POSITIONS.includes(position);

/** Each document the form uploads, the column it lands in, and who must provide it. */
export const APPLICATION_DOCUMENTS = [
  { key: 'resume', column: 'resume_url', label: 'Resume' },
  { key: 'id', column: 'id_document_url', label: 'Government ID' },
  { key: 'cpr', column: 'cpr_card_url', label: 'CPR / First Aid card' },
  { key: 'tb', column: 'tb_result_url', label: 'TB test result' },
  { key: 'license', column: 'license_cert_url', label: 'Nursing license / certificate', onlyFor: LICENSE_REQUIRED_POSITIONS },
];

/** Optional certificates, kept as a list rather than one column. */
export const CERTIFICATION_DOCUMENT = 'certification';

/** The column a document key writes to; certifications append to certification_urls. */
export const documentColumn = (key) =>
  key === CERTIFICATION_DOCUMENT ? 'certification_urls' : (APPLICATION_DOCUMENTS.find((d) => d.key === key)?.column || null);

/** The documents an application for this position must have. */
export const requiredDocuments = (position) =>
  APPLICATION_DOCUMENTS.filter((d) => !d.onlyFor || d.onlyFor.includes(position));

/** Required documents the application does not have yet. */
export const missingDocuments = (app) =>
  requiredDocuments(app?.position_applied_for).filter((d) => !String(app?.[d.column] || '').trim());

/** Is an incomplete application still open to its applicant, as of `now`? */
export const applicationStillOpen = (app, now = new Date()) =>
  !!app && app.status === APPLICATION_INCOMPLETE &&
  Date.parse(app.created_at) >= now.getTime() - APPLICATION_OPEN_HOURS * 3600000;

/**
 * The fields the applicant may set when finishing, and nothing else: never
 * status, review fields, the documents (those come through the upload) or
 * anything the office writes.
 */
export const APPLICANT_FIELDS = [
  'first_name', 'last_name', 'email', 'phone', 'address', 'city', 'state', 'zip', 'date_of_birth',
  'position_applied_for', 'employment_type_desired', 'work_history',
  'cpr_expiry_date', 'tb_test_date', 'license_expiry_date',
  'consent_signature_name', 'consent_signed_at', 'form_language',
];

/**
 * The applicant's fields from a request, trimmed and bounded; dates empty → null.
 * @param {Record<string, unknown>} [fields]
 * @returns {Record<string, string|null>}
 */
export const applicantFieldsFrom = (fields = {}) => {
  /** @type {Record<string, string|null>} */
  const out = {};
  for (const k of APPLICANT_FIELDS) {
    if (!(k in (fields || {}))) continue;
    const v = fields[k];
    if (v === null || v === undefined || v === '') { out[k] = /date|_at$/.test(k) ? null : ''; continue; }
    out[k] = String(v).trim().slice(0, k === 'work_history' ? 8000 : 500);
  }
  return out;
};
